import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FeatureCard from 'app/components/FeatureCard';
import {
  selectCollapseAll,
  selectUnNestAll,
  selectUnnestParentIds
} from 'app/pages/Home/slice/selectors';
import { MonthlyInlineItem } from './MonthlyInlineItem';
import type { MonthlyTimelineItem } from '../utils/types';
import {
  getManuallyCollapsedCards,
  getManuallyExpandedCards,
  getMonthlyExpandKey,
  getMonthlyFeatures,
  getMonthlyRegularChildren,
  isMonthlyItemExpandable,
  setManuallyCollapsedCard,
  setManuallyExpandedCard
} from '../utils/monthlyExpandUtils';
import { useMonthlyTimelineSlice } from '../store/monthlyTimelineSlice';
import {
  MONTHLY_TREE_BRANCH_TO_CARD_GAP_PX,
  MONTHLY_TREE_CHILD_INDENT_PX,
  MONTHLY_TREE_CURVE_RADIUS_PX,
  MONTHLY_TREE_HORIZONTAL_BRANCH_PX
} from '../utils/monthlyLayoutConstants';
import {
  getMonthlyFeatureUniqueId,
  matchesMonthlyFeatureProduct,
  useMonthlyFeatureReorder
} from '../hooks/useMonthlyFeatureReorder';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyItemTreeProps {
  item: MonthlyTimelineItem;
  laneLabel: string;
  mustWinInstanceKey?: string;
}

type ExpandedRow =
  | { kind: 'child'; item: MonthlyTimelineItem; key: string }
  | { kind: 'feature'; item: MonthlyTimelineItem; key: string };

const MonthlyTreeChevronIcon = forwardRef<
  SVGSVGElement,
  { isExpanded: boolean }
>(function MonthlyTreeChevronIcon({ isExpanded }, ref) {
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="5"
      viewBox="0 0 8 5"
      fill="none"
      className={isExpanded ? styles.monthlyTreeChevronIconExpanded : undefined}
      aria-hidden
    >
      <path
        d="M0.000177796 0.932976C0.000177793 0.681669 0.0984847 0.446002 0.277525 0.268909C0.455258 0.0937756 0.690284 -0.00191777 0.939631 2.89058e-05C1.18898 0.00197558 1.4227 0.100296 1.59718 0.277376L3.94874 2.65823L6.28076 0.296896C6.4559 0.119816 6.68962 0.0208557 6.93896 0.0195491C7.18831 0.0176024 7.42334 0.113296 7.60042 0.288429C7.77946 0.465509 7.87842 0.701189 7.87842 0.953149C7.87842 1.1999 7.78271 1.43296 7.60888 1.60875L4.61279 4.64195C4.42659 4.82555 4.19156 4.92124 3.94222 4.9193C3.69287 4.91735 3.45915 4.81903 3.28467 4.64195L0.269045 1.58922C0.0958713 1.41343 0.000177798 1.18038 0.000177796 0.932976Z"
        fill="#78BBFA"
      />
    </svg>
  );
});

function toOptionalStringArray(
  value: string | string[] | undefined
): string[] | undefined {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    return [value];
  }

  return undefined;
}

export const MonthlyItemTree = memo(
  ({ item, laneLabel, mustWinInstanceKey }: MonthlyItemTreeProps) => {
    const dispatch = useDispatch();
    const { actions: monthlyTimelineActions } = useMonthlyTimelineSlice();
    const isCollapsed = useSelector(selectCollapseAll);
    const isUnNested = useSelector(selectUnNestAll);
    const unnestParentIds = useSelector(selectUnnestParentIds);
    const [cardExpanded, setCardExpanded] = useState(false);

    const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const chevronRef = useRef<HTMLButtonElement>(null);
    const chevronIconRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [connectorPaths, setConnectorPaths] = useState<string[]>([]);
    const [childColumnPaddingPx, setChildColumnPaddingPx] = useState(
      MONTHLY_TREE_CHILD_INDENT_PX
    );

    const regularChildren = useMemo(
      () => getMonthlyRegularChildren(item),
      [item]
    );
    const sourceFeatures = useMemo(() => getMonthlyFeatures(item), [item]);
    const {
      DRAG_DROP_REORDER_ENABLED,
      AFTER_LAST_DROP_TARGET,
      orderedFeatures,
      featureReorder,
      canEditFeatures,
      canReorderFeatures,
      lastReorderedFeatureId,
      draggedProduct,
      draggedProductIdRef
    } = useMonthlyFeatureReorder(item, sourceFeatures);
    const expandedRows = useMemo<ExpandedRow[]>(
      () => [
        ...regularChildren.map((child, index) => ({
          kind: 'child' as const,
          item: child,
          key: child.issueId || child.id || `child-${index}`
        })),
        ...orderedFeatures.map((feature, index) => ({
          kind: 'feature' as const,
          item: feature,
          key: getMonthlyFeatureUniqueId(feature) || `feature-${index}`
        }))
      ],
      [orderedFeatures, regularChildren]
    );
    const lastFeatureRowIndex = useMemo(() => {
      for (let index = expandedRows.length - 1; index >= 0; index -= 1) {
        if (expandedRows[index].kind === 'feature') {
          return index;
        }
      }
      return -1;
    }, [expandedRows]);
    const isExpandable = isMonthlyItemExpandable(item);
    const isFilterMode = unnestParentIds.length > 0;
    const expandKey = getMonthlyExpandKey(item, mustWinInstanceKey);
    const parentIssueId = item.issueId || '';
    const parentType = item.type || item.projectType || '';

    useEffect(() => {
      if (!isExpandable) return;

      const isManuallyExpanded = getManuallyExpandedCards().includes(expandKey);
      const isManuallyCollapsed =
        getManuallyCollapsedCards().includes(expandKey);

      if (isManuallyExpanded) {
        setCardExpanded(true);
        return;
      }

      if (isManuallyCollapsed) {
        setCardExpanded(false);
        return;
      }

      if (isFilterMode) {
        const composite = item.releasePhaseId
          ? `${item.issueId}-${item.releasePhaseId}-${Boolean(
              item.showOnTimeline
            )}`
          : item.issueId;
        const shouldExpand =
          unnestParentIds.includes(composite || '') ||
          unnestParentIds.includes(item.issueId || '');
        setCardExpanded(shouldExpand);
        return;
      }

      setCardExpanded(isUnNested);
    }, [
      expandKey,
      isExpandable,
      isFilterMode,
      isUnNested,
      item.issueId,
      item.releasePhaseId,
      item.showOnTimeline,
      mustWinInstanceKey,
      unnestParentIds
    ]);

    useEffect(() => {
      if (isCollapsed) {
        setCardExpanded(false);
      }
    }, [isCollapsed]);

    useEffect(() => {
      if (isCollapsed && isUnNested && isExpandable) {
        setCardExpanded(true);
      }
    }, [isCollapsed, isExpandable, isUnNested]);

    const toggleExpand = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();

        const nextExpanded = !cardExpanded;
        setCardExpanded(nextExpanded);
        setManuallyExpandedCard(expandKey, nextExpanded);
        setManuallyCollapsedCard(expandKey, !nextExpanded);
        dispatch(monthlyTimelineActions.bumpItemExpandVersion());
      },
      [cardExpanded, dispatch, expandKey, monthlyTimelineActions]
    );

    const calculateConnectorPaths = useCallback(() => {
      if (
        !chevronRef.current ||
        !containerRef.current ||
        !cardExpanded ||
        !isExpandable ||
        expandedRows.length === 0
      ) {
        setConnectorPaths([]);
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const chevronAnchor =
        chevronIconRef.current?.getBoundingClientRect() ??
        chevronRef.current.getBoundingClientRect();
      const trunkX =
        chevronAnchor.left - containerRect.left + chevronAnchor.width / 2;
      const chevronCenterY =
        chevronAnchor.top - containerRect.top + chevronAnchor.height / 2;
      setChildColumnPaddingPx(
        trunkX +
          MONTHLY_TREE_CURVE_RADIUS_PX +
          MONTHLY_TREE_HORIZONTAL_BRANCH_PX +
          MONTHLY_TREE_BRANCH_TO_CARD_GAP_PX
      );
      const curveRadius = MONTHLY_TREE_CURVE_RADIUS_PX;
      const horizontalBranchLengthPx = MONTHLY_TREE_HORIZONTAL_BRANCH_PX;
      const paths: string[] = [];

      const rowConnections = expandedRows
        .map(row => {
          const rowElement = rowRefs.current.get(row.key);
          if (!rowElement) return null;

          if (row.kind === 'feature') {
            const featureCard = rowElement.querySelector(
              '[data-testid^="feature-card-"]:not([data-testid*="checkmark"]):not([data-testid*="title"]):not([data-testid*="risk-level"]):not([data-testid*="label"]):not([data-testid*="overdue"])'
            );
            if (featureCard) {
              const featureRect = featureCard.getBoundingClientRect();
              return {
                y: featureRect.top - containerRect.top + featureRect.height / 2,
                endX: featureRect.left - containerRect.left
              };
            }
          }

          const rowRect = rowElement.getBoundingClientRect();
          return {
            y: rowRect.top - containerRect.top + rowRect.height / 2,
            endX: rowRect.left - containerRect.left
          };
        })
        .filter(Boolean) as Array<{ y: number; endX: number }>;

      if (!rowConnections.length) {
        setConnectorPaths([]);
        return;
      }

      const lastConnectionY = rowConnections[rowConnections.length - 1].y;
      const trunkEndY = lastConnectionY - 4;

      paths.push(`M ${trunkX} ${chevronCenterY - 2} L ${trunkX} ${trunkEndY}`);

      rowConnections.forEach(({ y }) => {
        paths.push(
          `M ${trunkX} ${y - curveRadius} Q ${trunkX} ${y}, ${
            trunkX + curveRadius
          } ${y} L ${trunkX + curveRadius + horizontalBranchLengthPx} ${y}`
        );
      });

      setConnectorPaths(paths);
    }, [cardExpanded, expandedRows, isExpandable]);

    useEffect(() => {
      const updatePaths = () => {
        calculateConnectorPaths();
      };

      updatePaths();
      const rafId = requestAnimationFrame(updatePaths);

      const observer = new ResizeObserver(updatePaths);

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      if (chevronRef.current) {
        observer.observe(chevronRef.current);
      }

      if (chevronIconRef.current) {
        observer.observe(chevronIconRef.current);
      }

      rowRefs.current.forEach(rowElement => {
        observer.observe(rowElement);
      });

      return () => {
        cancelAnimationFrame(rafId);
        observer.disconnect();
      };
    }, [calculateConnectorPaths, cardExpanded, expandedRows, orderedFeatures]);

    return (
      <div
        ref={cardExpanded && isExpandable ? containerRef : undefined}
        className={`${styles.monthlyItemTreeRoot} ${
          isExpandable ? styles.monthlyItemTreeHasChevron : ''
        } ${
          cardExpanded && isExpandable ? styles.monthlyItemTreeExpanded : ''
        }`}
      >
        {cardExpanded && isExpandable && (
          <svg className={styles.monthlyTreeConnectorSvg} aria-hidden>
            {connectorPaths.map((path, index) => (
              <path
                key={index}
                d={path}
                stroke="#78BBFA"
                strokeWidth="1"
                strokeLinecap={index === 0 ? 'butt' : 'round'}
                strokeLinejoin="round"
                fill="none"
              />
            ))}
          </svg>
        )}

        <div
          className={`${styles.monthlyItemTreeParentRow} ${styles.monthlyItemTreeParentRowMain}`}
        >
          {isExpandable && (
            <button
              ref={chevronRef}
              type="button"
              className={styles.monthlyTreeChevronButton}
              aria-label={
                cardExpanded ? 'Collapse nested items' : 'Expand nested items'
              }
              aria-expanded={cardExpanded}
              data-testid={`monthly-tree-expand-button-${
                item.issueId || item.id
              }`}
              onClick={toggleExpand}
            >
              <MonthlyTreeChevronIcon
                ref={chevronIconRef}
                isExpanded={cardExpanded}
              />
            </button>
          )}
          <div className={styles.monthlyItemTreeParentItem}>
            <MonthlyInlineItem item={item} laneLabel={laneLabel} />
          </div>
        </div>

        {cardExpanded && isExpandable && (
          <div
            className={styles.monthlyTreeExpandedBody}
            data-testid={`monthly-tree-children-container-${parentIssueId}`}
          >
            <div
              className={styles.monthlyTreeChildrenColumn}
              style={{ paddingLeft: `${childColumnPaddingPx}px` }}
              onDragOver={
                DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                  ? e => {
                      if (draggedProductIdRef.current === null) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }
                  : undefined
              }
            >
              {expandedRows.map((row, index) => {
                if (row.kind === 'child') {
                  return (
                    <div
                      key={row.key}
                      ref={el => {
                        if (el) {
                          rowRefs.current.set(row.key, el);
                        } else {
                          rowRefs.current.delete(row.key);
                        }
                      }}
                      className={styles.monthlyTreeChildRow}
                      onClick={event => event.stopPropagation()}
                    >
                      <MonthlyInlineItem
                        item={row.item}
                        laneLabel={laneLabel}
                        isChild
                      />
                    </div>
                  );
                }

                const feature = row.item;
                const childUniqueId = row.key;
                const featureIndex = orderedFeatures.findIndex(
                  f => getMonthlyFeatureUniqueId(f) === childUniqueId
                );
                const featureWithProduct = feature as MonthlyTimelineItem & {
                  productId?: string;
                };
                const isDragging = featureReorder.draggedId === childUniqueId;
                const isDragOver = featureReorder.dragOverId === childUniqueId;
                const showDropLineAbove =
                  featureReorder.dropPosition?.index === featureIndex;
                const showDropLineBelow =
                  featureReorder.dropPosition?.index === featureIndex + 1;
                const isJustReordered =
                  lastReorderedFeatureId === childUniqueId;
                const isDeniedTarget =
                  draggedProduct !== null &&
                  featureWithProduct.productId !== draggedProduct;

                return (
                  <div
                    key={row.key}
                    ref={el => {
                      if (el) {
                        rowRefs.current.set(row.key, el);
                      } else {
                        rowRefs.current.delete(row.key);
                      }
                    }}
                    data-testid={`monthly-feature-wrapper-${childUniqueId}`}
                    title={
                      canEditFeatures && !canReorderFeatures
                        ? 'Feature reordering is not available for this moment type'
                        : undefined
                    }
                    onDragOver={
                      DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                        ? e => {
                            if (
                              !matchesMonthlyFeatureProduct(
                                featureWithProduct.productId,
                                draggedProductIdRef.current
                              )
                            ) {
                              return;
                            }
                            featureReorder.handleDragOver(
                              e,
                              childUniqueId,
                              orderedFeatures
                            );
                          }
                        : undefined
                    }
                    onDragLeave={
                      DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                        ? e => featureReorder.handleDragLeave(e, childUniqueId)
                        : undefined
                    }
                    onDrop={
                      DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                        ? e => {
                            if (
                              !matchesMonthlyFeatureProduct(
                                featureWithProduct.productId,
                                draggedProductIdRef.current
                              )
                            ) {
                              return;
                            }
                            featureReorder.handleDrop(
                              e,
                              childUniqueId,
                              orderedFeatures
                            );
                          }
                        : undefined
                    }
                    className={`${styles.monthlyTreeChildRow} ${
                      styles.monthlyTreeFeatureRow
                    } ${styles.monthlyFeatureRow} ${
                      index === lastFeatureRowIndex
                        ? styles.monthlyTreeFeatureRowLast
                        : ''
                    } ${
                      isDragOver ? styles.monthlyFeatureDragOver : ''
                    }`.trim()}
                    style={{
                      zIndex: isDragging ? 10 : 'auto',
                      opacity: isDeniedTarget ? 0.35 : 1,
                      cursor: isDeniedTarget ? 'not-allowed' : undefined,
                      transition: 'opacity 0.15s ease'
                    }}
                    onClick={event => event.stopPropagation()}
                  >
                    {showDropLineAbove && (
                      <div
                        className={styles.monthlyFeatureDropIndicator}
                        style={{ top: 0, left: 0, right: 0 }}
                      />
                    )}
                    <div
                      draggable={
                        DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                      }
                      onDragStart={
                        DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                          ? e => {
                              draggedProductIdRef.current =
                                featureWithProduct.productId ?? null;
                              featureReorder.handleDragStart(e, childUniqueId);
                            }
                          : undefined
                      }
                      onDragEnd={() => {
                        draggedProductIdRef.current = null;
                        featureReorder.handleDragEnd();
                      }}
                      onKeyDown={
                        DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                          ? e => {
                              const sameProductItems = orderedFeatures.filter(
                                (
                                  f: MonthlyTimelineItem & {
                                    productId?: string;
                                  }
                                ) =>
                                  f.productId === featureWithProduct.productId
                              );
                              featureReorder.handleRowKeyDown(
                                e,
                                childUniqueId,
                                sameProductItems
                              );
                            }
                          : undefined
                      }
                      style={{
                        transition:
                          'opacity 0.25s cubic-bezier(0.25, 0.4, 0.25, 1)',
                        cursor:
                          DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                            ? isDeniedTarget
                              ? 'not-allowed'
                              : 'grab'
                            : 'default'
                      }}
                      className={`${styles.monthlyFeatureDragHandle} ${
                        isDragging ? styles.monthlyFeaturePlaceholder : ''
                      }`.trim()}
                    >
                      {isDragging ? (
                        <div
                          className={styles.monthlyFeaturePlaceholderInner}
                        />
                      ) : (
                        <FeatureCard
                          card={{
                            ...feature,
                            issueId:
                              feature.issueId ||
                              feature.featureId ||
                              feature.id,
                            title: feature.title || '',
                            categories: toOptionalStringArray(
                              feature.categories
                            ),
                            products: toOptionalStringArray(feature.products),
                            links: feature.links
                              ?.filter(link => link.type && link.reference)
                              .map(link => ({
                                type: link.type as string,
                                reference: link.reference as string
                              })),
                            relationships: [
                              {
                                type: 'is child of',
                                value: parentIssueId,
                                targetDate: item.dates?.targetStart
                              }
                            ],
                            parentReleasePhase: item.releasePhase,
                            parentDates: item.dates,
                            parentShowOnTimeline: item.showOnTimeline
                          }}
                          type={parentType}
                          isChild
                          level={2}
                          parentPriority=""
                          isQuarterExpanded
                          isUnNested={isUnNested}
                          isLast={index === expandedRows.length - 1}
                          isJustReordered={isJustReordered}
                          isMonthlyInline
                        />
                      )}
                    </div>
                    {showDropLineBelow && (
                      <div
                        className={styles.monthlyFeatureDropIndicator}
                        style={{ bottom: 0, left: 0, right: 0 }}
                      />
                    )}
                  </div>
                );
              })}

              {DRAG_DROP_REORDER_ENABLED &&
                canReorderFeatures &&
                orderedFeatures.length > 0 && (
                  <div
                    data-testid={`monthly-feature-wrapper-${AFTER_LAST_DROP_TARGET}`}
                    onDragOver={e => {
                      const lastFeature = orderedFeatures[
                        orderedFeatures.length - 1
                      ] as MonthlyTimelineItem & {
                        productId?: string;
                      };
                      if (
                        !matchesMonthlyFeatureProduct(
                          lastFeature?.productId,
                          draggedProductIdRef.current
                        )
                      ) {
                        return;
                      }
                      featureReorder.handleDragOver(
                        e,
                        AFTER_LAST_DROP_TARGET,
                        orderedFeatures
                      );
                    }}
                    onDragLeave={e =>
                      featureReorder.handleDragLeave(e, AFTER_LAST_DROP_TARGET)
                    }
                    onDrop={e => {
                      const lastFeature = orderedFeatures[
                        orderedFeatures.length - 1
                      ] as MonthlyTimelineItem & {
                        productId?: string;
                      };
                      if (
                        !matchesMonthlyFeatureProduct(
                          lastFeature?.productId,
                          draggedProductIdRef.current
                        )
                      ) {
                        return;
                      }
                      featureReorder.handleDrop(
                        e,
                        AFTER_LAST_DROP_TARGET,
                        orderedFeatures
                      );
                    }}
                    className={styles.monthlyFeatureAfterLastDropZone}
                    style={{
                      minHeight:
                        featureReorder.dragOverId === AFTER_LAST_DROP_TARGET
                          ? 24
                          : 0
                    }}
                  >
                    {featureReorder.dragOverId === AFTER_LAST_DROP_TARGET &&
                      featureReorder.dropPosition?.index ===
                        orderedFeatures.length && (
                        <div
                          className={styles.monthlyFeatureDropIndicator}
                          style={{ top: 0, left: 0, right: 0 }}
                        />
                      )}
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    );
  }
);
